import { Wifi, WifiOff, RefreshCw } from "lucide-react";
import { FC } from "react";
import { Button } from "./ui/button";
import { useConnectionStatus } from "../hooks/useConnectionStatus";

interface ConnectionStatusProps {
  showWhenOnline?: boolean;
  className?: string;
}

const ConnectionStatus: FC<ConnectionStatusProps> = ({ 
  showWhenOnline = false, 
  className = "" 
}) => {
  const { 
    isOnline, 
    retryCount, 
    checkConnection, 
    isCheckingConnection 
  } = useConnectionStatus();

  // Don't show anything if online and showWhenOnline is false
  if (isOnline && !showWhenOnline) {
    return null;
  }

  const getStatusIcon = () => {
    if (isCheckingConnection) {
      return <RefreshCw className="w-4 h-4 animate-spin" />;
    }
    return isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />;
  };

  const getStatusText = () => {
    if (isCheckingConnection) {
      return "Checking connection...";
    }
    if (isOnline) {
      return "Connected to server";
    }
    if (retryCount > 0) {
      return `Connection lost (retry ${retryCount}/3)`;
    }
    return "Connection lost";
  };

  const getStatusColor = () => {
    if (isCheckingConnection) {
      return "text-blue-600 bg-blue-50 border-blue-200";
    }
    return isOnline 
      ? "text-green-600 bg-green-50 border-green-200" 
      : "text-red-600 bg-red-50 border-red-200";
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${getStatusColor()} ${className}`}>
      <div className="flex items-center space-x-2">
        {getStatusIcon()}
        <span className="text-sm font-medium">
          {getStatusText()}
        </span>
      </div>
      
      {!isOnline && (
        <Button
          variant="outline"
          size="sm"
          onClick={checkConnection}
          disabled={isCheckingConnection}
          className="h-7 px-2 text-xs"
        >
          {isCheckingConnection ? (
            <>
              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default ConnectionStatus;
